import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowRight16Regular, ArrowClockwise16Regular, Mail16Regular, Open16Regular } from '@fluentui/react-icons';
import { toast } from 'react-toastify';
import axiosInstance from '../../apiServices/axios';
import {
    WORKSPACE_PULSE_ADMIN,
    WORKSPACE_PULSE_GENERATE,
    WORKSPACE_PULSE_RECIPIENTS,
    WORKSPACE_PULSE_SCREEN,
} from '../../apiServices/api_routes';
import AttentionItemsPanel from './AttentionItemsPanel';
import WorkspacePulseRecipientsModal from './WorkspacePulseRecipientsModal';
import styles from './WorkspacePulse.module.css';
import { WORKSPACE_PULSE_ACCESS_TOAST, WORKSPACE_PULSE_PROJECT_ACCESS_TOAST } from '../../utils/workspacePulseProjectAccess';
import { navigatePulseProjectLink, toPulseAppPath } from '../../utils/pulseDeepLinkProject';
import { resolveWorkspacePulseWorkspaceId } from '../../utils/resolveWorkspacePulseWorkspaceId';
import { stashDeferredToast } from '../../utils/deferredToast';

function toAppPath(deepLink) {
    return toPulseAppPath(deepLink);
}

function featureBadgeClass(featureType) {
    const key = String(featureType || 'Task').toLowerCase();
    if (key.includes('action')) return styles.featureBadgeAction;
    if (key.includes('irl') || key.includes('information')) return styles.featureBadgeIrl;
    if (key.includes('task')) return styles.featureBadgeTask;
    return styles.featureBadgeDefault;
}

function projectDisplayName(projectOrItem) {
    return projectOrItem?.project_name_display || projectOrItem?.project_name || '';
}

function IssueMeta({ item }) {
    const status = item.status_display || item.status;
    const time = item.time_label;
    const assignee = item.assignee_name;

    return (
        <div className={styles.issueMetaRow}>
            <span className={featureBadgeClass(item.feature_type)}>{item.feature_type || 'Task'}</span>
            {status ? (
                <>
                    <span className={styles.metaText}>·</span>
                    <span className={styles.metaText}>{status}</span>
                </>
            ) : null}
            {time ? (
                <>
                    <span className={styles.metaText}>·</span>
                    <span className={styles.metaText}>{time}</span>
                </>
            ) : null}
            {assignee ? (
                <>
                    <span className={styles.metaText}>·</span>
                    <span className={styles.metaAssignee}>{assignee}</span>
                </>
            ) : null}
        </div>
    );
}

function ProjectCard({ project, onNavigate }) {
    const [expanded, setExpanded] = useState(false);
    const visibleItems = expanded ? project.attention_items : (project.attention_items || []).slice(0, 3);
    const hiddenCount = Math.max(0, (project.attention_items || []).length - 3);

    return (
        <article className={styles.projectCard}>
            <div className={styles.projectCardBody}>
                <div className={styles.projectHeader}>
                    <button
                        type="button"
                        className={styles.projectTitleButton}
                        onClick={() => onNavigate(project.project_url, project.can_open_project)}
                    >
                        <span className={styles.projectName}>{projectDisplayName(project)}</span>
                    </button>
                    <p className={styles.projectStats}>{project.stats_line}</p>
                </div>

                {project.project_summary ? (
                    <div className={styles.projectSection}>
                        <p className={styles.sectionLabel}>Why it needs attention</p>
                        <p className={styles.projectSummary}>{project.project_summary}</p>
                    </div>
                ) : null}

                {(project.attention_items || []).length > 0 ? (
                    <div className={styles.projectSection}>
                        <p className={styles.sectionLabel}>Key Issues</p>
                        <div className={styles.issueList}>
                            {visibleItems.map(item => (
                                <button
                                    key={`${item.rank}-${item.item_id}-${item.title}`}
                                    type="button"
                                    className={styles.issueRow}
                                    onClick={() => {
                                        const path = toAppPath(item.deep_link);
                                        if (path) onNavigate(path, item.can_open_project);
                                    }}
                                >
                                    <p className={styles.issueTitle}>{item.title}</p>
                                    <IssueMeta item={item} />
                                </button>
                            ))}
                        </div>
                        {hiddenCount > 0 ? (
                            <button type="button" className={styles.expandLink} onClick={() => setExpanded(prev => !prev)}>
                                {expanded ? 'Show less' : `+${hiddenCount} more issue${hiddenCount === 1 ? '' : 's'}`}
                            </button>
                        ) : null}
                    </div>
                ) : null}
            </div>

            <div className={styles.projectCardFooter}>
                <button type="button" className={styles.viewProjectLink} onClick={() => onNavigate(project.project_url, project.can_open_project)}>
                    View Project
                    <Open16Regular />
                </button>
            </div>
        </article>
    );
}

const WorkspacePulsePage = ({ workspaceId: workspaceIdProp, embedded = false }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const { activeWorkspaceId: activeWorkspaceIdFromState, memberships, workspacesLoading, access, user } = useSelector(
        state => ({
            activeWorkspaceId: state.user?.activeWorkspaceId || '',
            memberships: state.user?.memberships || [],
            workspacesLoading: Boolean(state.user?.workspacesLoading),
            access: state.user?.access,
            user: state.user?.user,
        }),
    );
    const workspaceId =
        workspaceIdProp ||
        resolveWorkspacePulseWorkspaceId({
            searchParams,
            activeWorkspaceId: activeWorkspaceIdFromState,
            workspacesLoading,
            memberships,
        });
    const authReady = Boolean(user?.id && access);

    const [loading, setLoading] = useState(true);
    const [accessDenied, setAccessDenied] = useState(false);
    const [screen, setScreen] = useState(null);
    const [rolloutEnabled, setRolloutEnabled] = useState(false);
    const [canManageRecipients, setCanManageRecipients] = useState(false);
    const [canManagePulseAdmin, setCanManagePulseAdmin] = useState(false);
    const [canRegenerate, setCanRegenerate] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [panelOpen, setPanelOpen] = useState(false);
    const [recipientsOpen, setRecipientsOpen] = useState(false);
    const [recipients, setRecipients] = useState([]);
    const [members, setMembers] = useState([]);
    const [recipientsLoading, setRecipientsLoading] = useState(false);
    const [recipientsSaving, setRecipientsSaving] = useState(false);

    const loadScreen = useCallback(async () => {
        if (!workspaceId || !authReady) return;
        setLoading(true);
        setAccessDenied(false);
        try {
            const { data } = await axiosInstance.get(WORKSPACE_PULSE_SCREEN, {
                params: { workspace_id: workspaceId },
            });
            setRolloutEnabled(Boolean(data?.rollout_enabled));
            setCanManageRecipients(Boolean(data?.can_manage_recipients));
            setCanManagePulseAdmin(Boolean(data?.can_manage_pulse_admin));
            setCanRegenerate(Boolean(data?.can_regenerate));
            setRecipients(Array.isArray(data?.email_recipients) ? data.email_recipients : []);
            setScreen(data?.screen || null);
        } catch (error) {
            if (error?.response?.status === 403) {
                setAccessDenied(true);
                setScreen(null);
                return;
            }
            toast.error(error?.response?.data?.detail || 'Workspace Pulse could not be loaded.', { position: 'top-right' });
            setScreen(null);
        } finally {
            setLoading(false);
        }
    }, [workspaceId, authReady]);

    useEffect(() => {
        if (!authReady) return;
        void loadScreen();
    }, [authReady, loadScreen]);

    useEffect(() => {
        if (!accessDenied) return;
        stashDeferredToast(WORKSPACE_PULSE_ACCESS_TOAST);
        navigate('/project', { replace: true });
    }, [accessDenied, navigate]);

    const loadRecipientsData = useCallback(async () => {
        if (!workspaceId) return;
        if (!canManagePulseAdmin) {
            return;
        }
        setRecipientsLoading(true);
        try {
            const [adminRes, membersRes] = await Promise.all([
                axiosInstance.get(WORKSPACE_PULSE_ADMIN(workspaceId)),
                axiosInstance.get(`accounts/workspaces/${workspaceId}/admin/members/`),
            ]);
            setRecipients(Array.isArray(adminRes?.data?.recipients) ? adminRes.data.recipients : []);
            setMembers(Array.isArray(membersRes?.data?.members) ? membersRes.data.members : []);
        } catch (error) {
            toast.error('Failed to load Workspace Pulse recipients.', { position: 'top-right' });
        } finally {
            setRecipientsLoading(false);
        }
    }, [workspaceId, canManagePulseAdmin]);

    useEffect(() => {
        if (recipientsOpen) void loadRecipientsData();
    }, [recipientsOpen, loadRecipientsData]);

    const handleNavigate = useCallback(
        async (target, canOpen = true) => {
            if (canOpen === false) {
                stashDeferredToast(WORKSPACE_PULSE_PROJECT_ACCESS_TOAST);
                navigate('/project', { replace: true });
                return;
            }
            const ok = await navigatePulseProjectLink(dispatch, navigate, target);
            if (!ok) {
                stashDeferredToast(WORKSPACE_PULSE_PROJECT_ACCESS_TOAST);
                navigate('/project', { replace: true });
            }
        },
        [dispatch, navigate],
    );

    const handleRegenerate = async () => {
        if (!workspaceId || regenerating) return;
        setRegenerating(true);
        try {
            const { data } = await axiosInstance.post(WORKSPACE_PULSE_GENERATE(workspaceId), {
                force_materialize: true,
            });
            const materialized = data?.projects_materialized ?? data?.materialization?.projects_scanned;
            const message =
                materialized != null
                    ? `Workspace Pulse regenerated from ${materialized} projects`
                    : 'Workspace Pulse report regenerated from all projects';
            toast.success(message, { position: 'top-right' });
            await loadScreen();
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Could not regenerate Workspace Pulse. Try again.', {
                position: 'top-right',
            });
        } finally {
            setRegenerating(false);
        }
    };

    const handleSaveRecipients = async userIds => {
        setRecipientsSaving(true);
        try {
            const { data } = await axiosInstance.patch(WORKSPACE_PULSE_RECIPIENTS(workspaceId), { user_ids: userIds });
            setRecipients(Array.isArray(data?.recipients) ? data.recipients : []);
            setRecipientsOpen(false);
            toast.success('Workspace Pulse recipients updated', { position: 'top-right' });
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Could not update Workspace Pulse recipients. Try again.', {
                position: 'top-right',
            });
        } finally {
            setRecipientsSaving(false);
        }
    };

    if (!workspaceId) {
        return <div className={styles.centered}>Select a workspace to view Workspace Pulse.</div>;
    }

    if (!authReady || loading) {
        return <div className={styles.centered}>Loading Workspace Pulse…</div>;
    }

    if (accessDenied) {
        return <div className={styles.centered}>Opening Lens…</div>;
    }

    if (!rolloutEnabled) {
        return <div className={styles.centered}>Workspace Pulse is not enabled for this workspace yet.</div>;
    }

    if (!screen) {
        return (
            <div className={styles.centered}>
                Workspace Pulse could not be loaded.
                <div style={{ marginTop: 12 }}>
                    <button type="button" className={styles.headerButton} onClick={() => void loadScreen()}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const leadership = screen.leadership_focus || {};
    const headline = screen.headline || {};

    return (
        <div className={embedded ? `${styles.workspacePulsePage} ${styles.workspacePulseEmbedded}` : styles.workspacePulsePage}>
            <div className={styles.topBar}>
                <div className={styles.topBarInner}>
                    <div className={styles.titleBlock}>
                        <h1>Workspace Pulse</h1>
                        <p>{screen.send_date_display || screen.send_date}</p>
                    </div>
                    <div className={styles.headerActions}>
                        {canManagePulseAdmin ? (
                            <button type="button" className={styles.headerButton} onClick={() => setRecipientsOpen(true)}>
                                <Mail16Regular />
                                Email Recipients
                            </button>
                        ) : null}
                        {canRegenerate ? (
                            <button
                                type="button"
                                className={styles.headerButton}
                                onClick={() => void handleRegenerate()}
                                disabled={regenerating}
                            >
                                <ArrowClockwise16Regular />
                                {regenerating ? 'Regenerating all projects…' : 'Regenerate'}
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>

            <div className={styles.mainArea}>
                <div className={styles.contentShell}>
                    {screen.quiet ? (
                        <div className={styles.headlineBlock}>
                            <h2 className={styles.headlinePrimary}>No significant workspace attention signals today</h2>
                            <p className={styles.executiveSummary}>
                                Pulse has not identified any priority signals requiring leadership attention across the current workspace scope.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className={styles.headlineBlock}>
                                <div className={styles.headlineRow}>
                                    <h2 className={styles.headlinePrimary}>{headline.projects_requiring_attention} projects require attention</h2>
                                    <p className={styles.headlineSecondary}>{headline.priority_signals_identified} priority signals identified</p>
                                </div>
                                {screen.executive_summary ? <p className={styles.executiveSummary}>{screen.executive_summary}</p> : null}
                            </div>

                            {(leadership.todays_decision || leadership.this_week || leadership.escalation) && (
                                <div className={styles.sectionBlock}>
                                    <p className={styles.sectionLabel}>Leadership Focus</p>
                                    <div className={styles.leadershipGrid}>
                                        {leadership.todays_decision ? (
                                            <div className={styles.leadershipCard}>
                                                <p className={styles.leadershipCardLabel}>
                                                    <span className={`${styles.leadershipDot} ${styles.leadershipDotDecision}`} />
                                                    Today&apos;s Decision
                                                </p>
                                                <p className={styles.leadershipCardBody}>{leadership.todays_decision}</p>
                                            </div>
                                        ) : null}
                                        {leadership.this_week ? (
                                            <div className={styles.leadershipCard}>
                                                <p className={styles.leadershipCardLabel}>
                                                    <span className={`${styles.leadershipDot} ${styles.leadershipDotWeek}`} />
                                                    This Week
                                                </p>
                                                <p className={styles.leadershipCardBody}>{leadership.this_week}</p>
                                            </div>
                                        ) : null}
                                        {leadership.escalation ? (
                                            <div className={styles.leadershipCard}>
                                                <p className={styles.leadershipCardLabel}>
                                                    <span className={`${styles.leadershipDot} ${styles.leadershipDotEscalation}`} />
                                                    Escalation
                                                </p>
                                                <p className={styles.leadershipCardBody}>{leadership.escalation}</p>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            )}

                            {(screen.projects || []).length > 0 ? (
                                <div className={styles.sectionBlock}>
                                    <div className={styles.sectionHeaderRow}>
                                        <p className={styles.sectionLabel}>Projects Requiring Attention</p>
                                        <p className={styles.sectionHint}>Ordered by leadership priority</p>
                                    </div>
                                    <div className={styles.projectList}>
                                        {(screen.projects || []).map(project => (
                                            <ProjectCard key={project.project_id} project={project} onNavigate={handleNavigate} />
                                        ))}
                                    </div>
                                </div>
                            ) : null}

                            {(screen.portfolio_themes || []).length > 0 ? (
                                <div className={styles.sectionBlock}>
                                    <p className={styles.sectionLabel}>Portfolio Themes</p>
                                    <p className={styles.themesIntro}>
                                        Pulse has identified recurring patterns across the selected workspace attention signals.
                                    </p>
                                    <div className={styles.themesCard}>
                                        {(screen.portfolio_themes || []).map((theme, index) => (
                                            <div key={`${theme.title}-${index}`} className={styles.themeRow}>
                                                <div className={styles.themeContent}>
                                                    <p className={styles.themeTitle}>{theme.title}</p>
                                                    {theme.summary && theme.summary !== theme.title ? (
                                                        <p className={styles.themeSummary}>{theme.summary}</p>
                                                    ) : null}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null}

                            {screen.attention_items_total > 0 ? (
                                <div className={styles.viewAllWrap}>
                                    <button type="button" className={styles.viewAllButton} onClick={() => setPanelOpen(true)}>
                                        <ArrowRight16Regular />
                                        View all {screen.attention_items_total} attention items
                                    </button>
                                </div>
                            ) : null}
                        </>
                    )}

                    {screen.scope_disclaimer ? <p className={styles.scopeDisclaimer}>{screen.scope_disclaimer}</p> : null}
                </div>
            </div>

            <AttentionItemsPanel
                open={panelOpen}
                items={screen.attention_items_full}
                total={screen.attention_items_total}
                onClose={() => setPanelOpen(false)}
                onNavigate={handleNavigate}
            />

            <WorkspacePulseRecipientsModal
                open={recipientsOpen}
                loading={recipientsLoading}
                saving={recipientsSaving}
                members={members}
                recipients={recipients}
                readOnly={!canManageRecipients}
                onClose={() => setRecipientsOpen(false)}
                onSave={handleSaveRecipients}
            />
        </div>
    );
};

export default WorkspacePulsePage;
